import { useMemo, useState } from 'react';
import { Download, Plus, Trash2, X } from 'lucide-react';
import {
  Button,
  Checkbox,
  DataTable,
  Input,
  RadixSelect,
  SectionCard,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  RequiredMark,
  type DataTableColumn,
  type RowActionItem,
  RowActions,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import {
  downloadEventApplications,
  useEventCollectionFieldMutation,
  useEventCollectionFieldsQuery,
} from '../hooks/useEventCollectionFieldsQuery';
import {
  COLLECTION_MALLS,
  COLLECTION_MALL_LABELS,
  DEFAULT_COLLECTION_MALL,
  type CollectionMall,
} from '@/features/catalog/productCollection/collectionMalls';
import { CollectionFieldPreview } from './CollectionFieldPreview';
import {
  COLLECTION_FIELD_TYPES,
  COLLECTION_FIELD_TYPE_LABELS,
  isChoiceType,
  normalizeMaxLength,
  parseOptions,
  serializeOptions,
  type CollectionFieldType,
  type EventCollectionField,
  type EventCollectionFieldPayload,
} from '../types/collectionField';

interface Props {
  eventId: string;
}

/** 몰 탭별 수집 항목 — 활성 몰의 항목을 조회·CRUD 한다. */
interface MallProps {
  eventId: string;
  mall: CollectionMall;
}

interface NewFieldDraft {
  label: string;
  fieldType: CollectionFieldType;
  required: boolean;
  sensitive: boolean;
  /** 선택형(RADIO/SELECT) 보기 목록 — 편집용 문자열 배열. */
  options: string[];
  /** 자유 입력형 글자수 제한(빈 문자열=무제한). */
  maxLength: string;
}

const EMPTY_NEW: NewFieldDraft = {
  label: '',
  fieldType: 'TEXT',
  required: false,
  sensitive: false,
  options: [''],
  maxLength: '',
};

const typeOptions = COLLECTION_FIELD_TYPES.map((t) => ({
  value: t,
  label: COLLECTION_FIELD_TYPE_LABELS[t],
}));

/**
 * 기존 필드 → payload (toggle 등 부분변경 시 나머지 보존). mall 은 행의 소속 몰을
 * 유지하되 응답에 없으면 활성 탭 몰로 폴백(항목2 — 행단위 CRUD 라 mall 동반).
 */
const toPayload = (
  field: EventCollectionField,
  mall: CollectionMall,
  patch: Partial<EventCollectionFieldPayload>,
): EventCollectionFieldPayload => ({
  mall: field.mall ?? mall,
  fieldKey: field.fieldKey,
  label: field.label,
  fieldType: field.fieldType,
  collect: field.collect,
  required: field.required,
  sensitive: field.sensitive,
  sortOrder: field.sortOrder,
  optionsJson: field.optionsJson,
  maxLength: field.maxLength,
  ...patch,
});

interface RowConfigCellProps {
  field: EventCollectionField;
  disabled: boolean;
  onCommitOptions: (optionsJson: string) => void;
  onCommitMaxLength: (maxLength: number | null) => void;
}

/**
 * 기존 행의 옵션/제한 인라인 편집 — 선택형은 보기 목록(추가/삭제), 자유형은 글자수 제한.
 * 키 입력마다 PUT 하지 않도록 로컬 버퍼에 담고 commit(보기 blur / 제한 blur) 시 1회 PUT 한다.
 * 선택형 보기가 모두 비면 be-catalog 가 거부하므로 commit 하지 않고 직전 값을 유지한다.
 */
const RowConfigCell = ({ field, disabled, onCommitOptions, onCommitMaxLength }: RowConfigCellProps) => {
  const choice = isChoiceType(field.fieldType);
  const [options, setOptions] = useState<string[]>(() => {
    const parsed = parseOptions(field.optionsJson);
    return parsed.length > 0 ? parsed : [''];
  });
  const [maxLengthText, setMaxLengthText] = useState<string>(
    field.maxLength != null ? String(field.maxLength) : '',
  );

  // 서버 값이 외부에서 바뀌면(다른 토글의 PUT 무효화 등) 로컬 버퍼를 동기화.
  const serverOptions = field.optionsJson ?? '';
  const serverMax = field.maxLength ?? '';
  const [syncKey, setSyncKey] = useState(`${serverOptions}|${serverMax}`);
  if (syncKey !== `${serverOptions}|${serverMax}`) {
    const parsed = parseOptions(field.optionsJson);
    setOptions(parsed.length > 0 ? parsed : ['']);
    setMaxLengthText(field.maxLength != null ? String(field.maxLength) : '');
    setSyncKey(`${serverOptions}|${serverMax}`);
  }

  const commitOptions = (next: string[]) => {
    const json = serializeOptions(next);
    if (json !== null) onCommitOptions(json);
  };

  if (!choice) {
    return (
      <Input
        type="number"
        min={1}
        value={maxLengthText}
        onChange={(e) => setMaxLengthText(e.target.value)}
        onBlur={() => onCommitMaxLength(normalizeMaxLength(field.fieldType, Number(maxLengthText)))}
        placeholder="무제한"
        disabled={disabled}
        className="h-8 w-[110px]"
        aria-label={`${field.label} 글자수 제한`}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid="row-options-editor">
      {options.map((opt, index) => (
        <div key={index} className="flex items-center gap-1">
          <Input
            value={opt}
            onChange={(e) =>
              setOptions((prev) => prev.map((o, i) => (i === index ? e.target.value : o)))
            }
            onBlur={() => commitOptions(options)}
            placeholder={`보기 ${index + 1}`}
            disabled={disabled}
            className="h-8 max-w-[170px]"
            aria-label={`${field.label} 보기 ${index + 1}`}
          />
          <Button
            variant="ghost"
            size="sm"
            className="!h-8 !px-1.5"
            onClick={() => {
              const next = options.filter((_, i) => i !== index);
              const safe = next.length > 0 ? next : [''];
              setOptions(safe);
              commitOptions(safe);
            }}
            disabled={disabled || options.length <= 1}
            aria-label={`${field.label} 보기 ${index + 1} 삭제`}
          >
            <Trash2 size={13} aria-hidden="true" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="!h-7 self-start !px-2 text-[12px]"
        onClick={() => setOptions((prev) => [...prev, ''])}
        disabled={disabled}
        aria-label={`${field.label} 보기 추가`}
      >
        <Plus size={12} aria-hidden="true" />
        보기 추가
      </Button>
    </div>
  );
};

/**
 * 이벤트 응모/수집 항목 설정(몰별) — 필드 CUD + 정렬(↑↓) + collect/required 토글 + 미리보기.
 * 캡처 시점은 체크아웃(SF). 여기서는 어떤 항목을 어떻게 수집할지 활성 몰별로 정의한다.
 */
const MallEventCollectionFields = ({ eventId, mall }: MallProps) => {
  const query = useEventCollectionFieldsQuery(eventId, mall);
  const { create, update, remove } = useEventCollectionFieldMutation(eventId, mall);

  const [draft, setDraft] = useState<NewFieldDraft>(EMPTY_NEW);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  // QA-137 — "결제 완료 상품만 추출" 옵션. 체크 시 PAID 주문의 응모만 내려받는다.
  const [exportPaidOnly, setExportPaidOnly] = useState(false);

  const validEventId = !!eventId && eventId !== 'new';

  const handleExport = async () => {
    setExporting(true);
    try {
      const { blob, filename } = await downloadEventApplications(eventId, {
        paidOnly: exportPaidOnly,
      });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
      toast.success('응모자 목록을 다운로드했습니다.');
    } catch (err) {
      toast.error(friendlyMessage(err, '응모자 추출에 실패했습니다.'));
    } finally {
      setExporting(false);
    }
  };

  // sortOrder 순 정렬본 — 정렬/미리보기 일관성.
  const fields = useMemo(
    () => (query.data ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [query.data],
  );
  const collectedCount = useMemo(
    () => (query.data ?? []).filter((f) => f.collect).length,
    [query.data],
  );

  const setNew = <K extends keyof NewFieldDraft>(key: K, val: NewFieldDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: val }));

  const draftIsChoice = isChoiceType(draft.fieldType);

  // 보기 목록 편집 — 추가/삭제/수정. 빈 줄 1개는 항상 유지(입력 진입점).
  const setOption = (index: number, val: string) =>
    setDraft((prev) => ({
      ...prev,
      options: prev.options.map((o, i) => (i === index ? val : o)),
    }));
  const addOption = () => setDraft((prev) => ({ ...prev, options: [...prev.options, ''] }));
  const removeOption = (index: number) =>
    setDraft((prev) => {
      const next = prev.options.filter((_, i) => i !== index);
      return { ...prev, options: next.length > 0 ? next : [''] };
    });

  const handleAdd = () => {
    if (!validEventId) {
      setError('먼저 이벤트를 저장한 뒤 수집 항목을 추가할 수 있습니다.');
      return;
    }
    if (draft.label.trim() === '') {
      setError('항목명을 입력하세요.');
      return;
    }
    // 선택형(RADIO/SELECT)은 보기 목록이 최소 1개 필요 — be-catalog 가 빈 optionsJson 을 거부한다.
    const optionsJson = draftIsChoice ? serializeOptions(draft.options) : null;
    if (draftIsChoice && optionsJson === null) {
      setError('선택형(라디오/드롭다운) 항목은 보기를 1개 이상 입력하세요.');
      return;
    }
    setError(null);
    const nextSort = fields.length > 0 ? Math.max(...fields.map((f) => f.sortOrder)) + 1 : 0;
    create.mutate(
      {
        // 항목2 — 활성 탭 몰에 귀속(행단위 CRUD).
        mall,
        // custom 필드 키 — 라벨 기반 슬러그 + 보장용 접미.
        fieldKey: `CUSTOM_${Date.now()}`,
        label: draft.label.trim(),
        fieldType: draft.fieldType,
        collect: true,
        required: draft.required,
        sensitive: draft.sensitive,
        sortOrder: nextSort,
        optionsJson,
        maxLength: normalizeMaxLength(draft.fieldType, Number(draft.maxLength)),
      },
      {
        onSuccess: () => {
          toast.success('수집 항목이 추가되었습니다.');
          setDraft(EMPTY_NEW);
        },
        onError: (err) => setError(friendlyMessage(err, '추가에 실패했습니다.')),
      },
    );
  };

  const patchField = (field: EventCollectionField, patch: Partial<EventCollectionFieldPayload>) => {
    update.mutate(
      { fieldId: field.fieldId, body: toPayload(field, mall, patch) },
      { onError: (err) => toast.error(friendlyMessage(err, '변경에 실패했습니다.')) },
    );
  };

  // 인접 필드와 sortOrder 를 교환(↑↓). 두 건의 PUT 으로 멱등 처리.
  const move = (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= fields.length) return;
    const a = fields[index];
    const b = fields[next];
    if (!a || !b) return;
    patchField(a, { sortOrder: b.sortOrder });
    patchField(b, { sortOrder: a.sortOrder });
  };

  const handleRemove = (field: EventCollectionField) => {
    remove.mutate(field.fieldId, {
      onSuccess: () => toast.success('수집 항목이 삭제되었습니다.'),
      onError: (err) => toast.error(friendlyMessage(err, '삭제에 실패했습니다.')),
    });
  };

  const busy = create.isPending || update.isPending || remove.isPending;

  const columns: DataTableColumn<EventCollectionField>[] = [
    {
      key: 'label',
      header: '항목명',
      cell: (row) => (
        <Input
          value={row.label}
          onChange={(e) => patchField(row, { label: e.target.value })}
          disabled={busy}
          className="h-8 max-w-[200px]"
          aria-label="항목명"
        />
      ),
    },
    {
      key: 'fieldType',
      header: '유형',
      width: 130,
      cell: (row) => (
        <RadixSelect
          value={row.fieldType}
          onChange={(v) => patchField(row, { fieldType: v as CollectionFieldType })}
          options={typeOptions}
          disabled={busy}
          aria-label="유형"
        />
      ),
    },
    {
      key: 'config',
      header: '옵션/제한',
      width: 240,
      cell: (row) => (
        <RowConfigCell
          field={row}
          disabled={busy}
          onCommitOptions={(json) => patchField(row, { optionsJson: json })}
          onCommitMaxLength={(len) => patchField(row, { maxLength: len })}
        />
      ),
    },
    {
      key: 'collect',
      header: '수집',
      width: 70,
      align: 'center',
      cell: (row) => (
        <Switch
          checked={row.collect}
          onCheckedChange={(v) => patchField(row, { collect: v })}
          disabled={busy}
          aria-label={`${row.label} 수집`}
        />
      ),
    },
    {
      key: 'required',
      header: '필수',
      width: 70,
      align: 'center',
      cell: (row) => (
        <Switch
          checked={row.required}
          onCheckedChange={(v) => patchField(row, { required: v })}
          disabled={busy || !row.collect}
          aria-label={`${row.label} 필수`}
        />
      ),
    },
    {
      key: 'sensitive',
      header: '민감',
      width: 70,
      align: 'center',
      cell: (row) => (
        <Switch
          checked={row.sensitive}
          onCheckedChange={(v) => patchField(row, { sensitive: v })}
          disabled={busy}
          aria-label={`${row.label} 민감정보`}
        />
      ),
    },
    {
      key: 'actions',
      header: '정렬',
      width: 56,
      align: 'right',
      cell: (row) => {
        const index = fields.findIndex((f) => f.fieldId === row.fieldId);
        const actions: RowActionItem[] = [
          { key: 'up', label: '위로', disabled: busy || index <= 0, onClick: () => move(index, -1) },
          {
            key: 'down',
            label: '아래로',
            disabled: busy || index >= fields.length - 1,
            onClick: () => move(index, 1),
          },
          {
            key: 'remove',
            label: '삭제',
            variant: 'danger',
            separatorBefore: true,
            disabled: busy,
            onClick: () => handleRemove(row),
          },
        ];
        return <RowActions triggerLabel="작업" actions={actions} />;
      },
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-4">
        {/* 새 항목 추가 */}
        <SectionCard title="수집 항목 추가">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                항목명
                <RequiredMark />
              </span>
              <Input
                value={draft.label}
                onChange={(e) => setNew('label', e.target.value)}
                placeholder="예: 수령인 이름"
                disabled={busy || !validEventId}
                className="max-w-[200px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                }}
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                유형
              </span>
              <div className="w-[130px]">
                <RadixSelect
                  value={draft.fieldType}
                  onChange={(v) => setNew('fieldType', v as CollectionFieldType)}
                  options={typeOptions}
                  disabled={busy || !validEventId}
                  aria-label="새 항목 유형"
                />
              </div>
            </div>
            {/* 자유 입력형만 글자수 제한 — 선택형은 보기 길이로 제한되어 비활성. */}
            <div className="flex flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                글자수 제한
              </span>
              <Input
                type="number"
                min={1}
                value={draft.maxLength}
                onChange={(e) => setNew('maxLength', e.target.value)}
                placeholder="무제한"
                disabled={busy || !validEventId || draftIsChoice}
                className="w-[110px]"
                aria-label="새 항목 글자수 제한"
              />
            </div>
            <label className="flex items-center gap-2 pb-2 text-[12px]" style={{ color: 'var(--content-default)' }}>
              <Switch
                checked={draft.required}
                onCheckedChange={(v) => setNew('required', v)}
                disabled={busy || !validEventId}
                aria-label="새 항목 필수"
              />
              필수
            </label>
            <label className="flex items-center gap-2 pb-2 text-[12px]" style={{ color: 'var(--content-default)' }}>
              <Switch
                checked={draft.sensitive}
                onCheckedChange={(v) => setNew('sensitive', v)}
                disabled={busy || !validEventId}
                aria-label="새 항목 민감정보"
              />
              민감
            </label>
            <Button
              size="sm"
              className="ml-auto"
              onClick={handleAdd}
              disabled={busy || !validEventId}
              data-testid="collection-field-add"
            >
              <Plus size={15} aria-hidden="true" />
              추가
            </Button>
          </div>
          {/* 선택형(RADIO/SELECT) 보기 목록 편집 — 유형이 선택형일 때만 노출, 필수. */}
          {draftIsChoice ? (
            <div className="mt-3 flex flex-col gap-2" data-testid="collection-field-options-editor">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                보기 목록
                <RequiredMark />
              </span>
              {draft.options.map((opt, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={opt}
                    onChange={(e) => setOption(index, e.target.value)}
                    placeholder={`보기 ${index + 1}`}
                    disabled={busy || !validEventId}
                    className="max-w-[260px]"
                    aria-label={`보기 ${index + 1}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="!h-8 !px-2"
                    onClick={() => removeOption(index)}
                    disabled={busy || !validEventId || draft.options.length <= 1}
                    aria-label={`보기 ${index + 1} 삭제`}
                  >
                    <X size={14} aria-hidden="true" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={addOption}
                disabled={busy || !validEventId}
                className="self-start"
                data-testid="collection-field-option-add"
              >
                <Plus size={14} aria-hidden="true" />
                보기 추가
              </Button>
            </div>
          ) : null}
          {!validEventId ? (
            <p className="mt-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
              먼저 이벤트를 저장한 뒤 수집 항목을 설정할 수 있습니다.
            </p>
          ) : null}
          {error ? (
            <p className="mt-2 text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert">
              {error}
            </p>
          ) : null}
        </SectionCard>

        {/* 항목 목록 */}
        <SectionCard
          title="수집 항목"
          description="수집/필수/민감 토글과 정렬(↑↓)을 즉시 반영합니다."
          aside={
            <div className="flex items-center gap-3">
              {/* QA-137 — '결제 완료 상품만 추출' 옵션. 체크 시 PAID 주문의 응모만 내려받는다. */}
              <label
                className="flex items-center gap-1.5 text-[12px]"
                style={{ color: 'var(--content-default)' }}
              >
                <Checkbox
                  checked={exportPaidOnly}
                  onCheckedChange={(v) => setExportPaidOnly(Boolean(v))}
                  disabled={!validEventId || exporting}
                  aria-label="결제 완료 상품만 추출"
                  data-testid="event-applications-export-paid-only"
                />
                결제 완료만
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleExport()}
                disabled={!validEventId || exporting}
                data-testid="event-applications-export"
              >
                <Download size={15} aria-hidden="true" />
                {exporting ? '추출 중…' : '응모자 추출(xlsx)'}
              </Button>
            </div>
          }
        >
          {/* QA-498 — 수집이 켜진 항목이 하나라도 있으면 FE 가 장바구니·바로구매 때 응모 정보 창(OPTION)을
              한 번 더 띄운다(storefront optionSheetGate). 응모가 필요 없는 이벤트는 꺼 두도록 안내한다. */}
          {validEventId && !query.isPending && !query.isError ? (
            <p
              className="mb-3 rounded-md p-3 text-[12px]"
              style={
                collectedCount > 0
                  ? { background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }
                  : { background: 'var(--surface-1)', color: 'var(--content-muted)' }
              }
              data-testid="collection-field-sheet-hint"
            >
              {collectedCount > 0
                ? `수집이 켜진 항목(${collectedCount}개)이 있어, 이 이벤트에 연결된 상품은 FE 에서 장바구니·바로구매를 누를 때 응모 정보 창이 한 번 더 뜹니다. 응모 정보가 필요 없는 이벤트(예: 포토카드 증정)는 "수집"을 모두 꺼 주세요. 몰 탭마다 따로 설정됩니다.`
                : '수집이 켜진 항목이 없어, 연결된 상품은 FE 에서 응모 정보 창 없이 바로 결제로 넘어갑니다. 응모 정보가 필요하면 받을 항목의 "수집"을 켜 주세요.'}
            </p>
          ) : null}
          {/* [event-form-fix] 등록 모드(query disabled)는 v5 isPending=true 라 영원한 스켈레톤이
              떠 "로딩 고장"처럼 보였다 — 실제 조회중일 때만 스켈레톤, 미저장은 빈 목록. */}
          {validEventId && query.isPending ? (
            <div role="status" aria-live="polite" className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-[40px] w-full" />
              ))}
            </div>
          ) : query.isError ? (
            <div
              role="alert"
              className="flex flex-col items-center gap-2 rounded-lg py-8 text-center text-sm"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
            >
              <p>수집 항목을 불러오지 못했습니다.</p>
              <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                다시 시도
              </Button>
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={fields}
              rowKey={(row) => row.fieldId}
              emptyState="수집 항목이 없습니다. 위에서 항목을 추가하세요."
              stickyHeader
              ariaLabel="이벤트 수집 항목 목록"
            />
          )}
        </SectionCard>
      </div>

      {/* 미리보기 */}
      <div className="lg:sticky lg:top-4 lg:self-start">
        <p className="mb-2 text-[13px] font-semibold" style={{ color: 'var(--content-strong)' }}>
          미리보기 (결제하기 응모폼)
        </p>
        <CollectionFieldPreview fields={fields} />
      </div>
    </div>
  );
};

/**
 * 항목2 — 이벤트 응모/수집 항목 4몰 탭(국내/일문/중문/영문).
 *
 * 각 몰을 독립된 수집 항목 세트로 관리한다(be-catalog per-mall 계약: 목록 `?mall=`,
 * 생성/수정 payload.mall). 동의(EventConsentBlock)는 enum 이라 mall 무관 공통이며 본
 * 폼에서 다루지 않는다. 백오피스 UI 한국어, 탭 라벨은 몰 구분 표기.
 */
export const EventCollectionFieldsForm = ({ eventId }: Props) => {
  const [activeMall, setActiveMall] = useState<CollectionMall>(DEFAULT_COLLECTION_MALL);

  return (
    <Tabs
      value={activeMall}
      onValueChange={(v) => setActiveMall(v as CollectionMall)}
      className="w-full"
    >
      <TabsList className="mb-4">
        {COLLECTION_MALLS.map((mall) => (
          <TabsTrigger
            key={mall}
            value={mall}
            data-testid={`event-collection-mall-tab-${mall}`}
          >
            {COLLECTION_MALL_LABELS[mall]}
          </TabsTrigger>
        ))}
      </TabsList>

      {COLLECTION_MALLS.map((mall) => (
        <TabsContent key={mall} value={mall}>
          <MallEventCollectionFields eventId={eventId} mall={mall} />
        </TabsContent>
      ))}
    </Tabs>
  );
};
